<app-modal
  [isOpen]="isOpen"
  (close)="onClose()"
  className="max-w-4xl mx-4 sm:mx-6"
>
  @if (priceList) {
    <div class="p-6 sm:p-8">
      <!-- Header -->
      <div class="flex items-start justify-between gap-6 mb-8">
        <div>
          <div class="flex items-center gap-2 mb-1">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              {{ priceList.name }}
            </h3>
            @if (priceList.isDefault) {
              <app-badge
                color="primary"
                size="sm"
                >Predeterminada</app-badge
              >
            }
            <span
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
            >
              {{ getCurrencyCode(priceList.currencyId) }}
            </span>
          </div>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            {{ priceList.description || 'Sin descripción' }}
          </p>
        </div>
      </div>

      <!-- Tabs -->
      <div class="border-b border-gray-200 dark:border-gray-700 mb-6">
        <button
          type="button"
          (click)="onTabChange('info')"
          class="whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
          [class.border-brand-500]="activeTab === 'info'"
          [class.text-brand-600]="activeTab === 'info'"
          [class.dark:text-brand-400]="activeTab === 'info'"
          [class.border-transparent]="activeTab !== 'info'"
          [class.text-gray-500]="activeTab !== 'info'"
          [class.hover:text-gray-700]="activeTab !== 'info'"
          [class.dark:text-gray-400]="activeTab !== 'info'"
        >
          Información
        </button>
        <button
          type="button"
          (click)="onTabChange('timeline')"
          class="whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
          [class.border-brand-500]="activeTab === 'timeline'"
          [class.text-brand-600]="activeTab === 'timeline'"
          [class.dark:text-brand-400]="activeTab === 'timeline'"
          [class.border-transparent]="activeTab !== 'timeline'"
          [class.text-gray-500]="activeTab !== 'timeline'"
          [class.hover:text-gray-700]="activeTab !== 'timeline'"
          [class.dark:text-gray-400]="activeTab !== 'timeline'"
        >
          Línea de tiempo
        </button>
      </div>

      <!-- Content -->
      @if (activeTab === 'info') {
        <div class="space-y-5">
          <!-- Metadata -->
          <section>
            <h4
              class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
            >
              Metadata
            </h4>
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <p class="text-xs text-gray-500">Productos</p>
                <p class="text-gray-800 dark:text-gray-200">
                  {{ priceList.totalItems ?? items.length }}
                </p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Creada</p>
                <p class="text-gray-800 dark:text-gray-200">
                  {{ formatDate(priceList.datetimeCreated) }}
                </p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Actualizada</p>
                <p class="text-gray-800 dark:text-gray-200">
                  {{ formatDate(priceList.datetimeUpdated) }}
                </p>
              </div>
            </div>
          </section>

          <!-- Products -->
          <section class="pt-4 border-t border-gray-200 dark:border-gray-700">
            <h4
              class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
            >
              Productos
            </h4>
            @if (loadingItems) {
              <div class="flex justify-center py-6">
                <div
                  class="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-500"
                ></div>
              </div>
            } @else if (items.length === 0) {
              <p
                class="text-center text-sm text-gray-500 dark:text-gray-400 py-6"
              >
                Sin productos en esta lista.
              </p>
            } @else {
              <div
                class="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <table class="min-w-full text-sm">
                  <thead class="bg-gray-50 dark:bg-gray-800/60">
                    <tr>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400"
                      >
                        Producto
                      </th>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400"
                      >
                        Tipo
                      </th>
                      <th
                        class="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400"
                      >
                        Precio base
                      </th>
                      <th
                        class="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400"
                      >
                        Config.
                      </th>
                      <th
                        class="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400"
                      >
                        Precio lista
                      </th>
                      <th
                        class="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400"
                      >
                        Min. cant.
                      </th>
                      <th
                        class="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400"
                      >
                        Total mín.
                      </th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100 dark:divide-white/5">
                    @for (it of items; track it.id) {
                      <tr>
                        <td class="px-3 py-2 text-gray-700 dark:text-gray-300">
                          <div class="flex flex-col">
                            <span class="text-sm font-medium">{{
                              getProductName(it.productId)
                            }}</span>
                            @if (getProductSku(it.productId)) {
                              <span
                                class="text-[10px] font-mono text-gray-400 dark:text-gray-500"
                                >{{ getProductSku(it.productId) }}</span
                              >
                            }
                          </div>
                        </td>
                        <td
                          class="px-3 py-2 text-xs text-gray-500 dark:text-gray-400"
                        >
                          {{ getPriceTypeLabel(it.priceType) }}
                        </td>
                        <td
                          class="px-3 py-2 text-right text-gray-500 dark:text-gray-400"
                        >
                          {{
                            formatPrecio(
                              getProductBasePrice(it.productId),
                              getProductCurrencyCode(it.productId)
                            )
                          }}
                        </td>
                        <td
                          class="px-3 py-2 text-right text-gray-700 dark:text-gray-300"
                        >
                          @if (it.priceType === 'fixed') {
                            {{
                              formatPrecio(
                                it.price,
                                getCurrencyCodeById(priceList.currencyId)
                              )
                            }}
                          } @else if (it.priceType === 'discount_amount') {
                            -{{
                              formatPrecio(
                                convertirMontoDescuento(it),
                                getCurrencyCodeById(priceList.currencyId)
                              )
                            }}
                          } @else if (it.priceType === 'discount_percent') {
                            {{ it.discountPercent }}%
                          }
                        </td>
                        <td
                          class="px-3 py-2 text-right font-semibold text-brand-600 dark:text-brand-400"
                        >
                          {{
                            formatPrecio(
                              getResolvedPrice(it),
                              getCurrencyCodeById(priceList.currencyId)
                            )
                          }}
                        </td>
                        <td
                          class="px-3 py-2 text-right text-xs text-gray-500 dark:text-gray-400"
                        >
                          {{ it.minQuantity ?? '-' }}
                        </td>
                        <td
                          class="px-3 py-2 text-right text-gray-700 dark:text-gray-300"
                        >
                          {{
                            formatPrecio(
                              getItemTotal(it),
                              getCurrencyCodeById(priceList.currencyId)
                            )
                          }}
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </section>
        </div>
      }
      <!-- ========== TAB: TIMELINE ========== -->
      @if (activeTab === 'timeline') {
        <app-audit-timeline
          [logs]="timelineLogs"
          [loading]="loadingTimeline"
          [error]="timelineError"
          [currentPage]="timelinePage"
          [totalPages]="timelineTotalPages"
          [totalElements]="timelineTotalElements"
          emptyMessage="No hay eventos de auditoría para este negocio."
          (pageChange)="loadTimeline($event)"
        />
      }
      <!-- end timeline tab -->
    </div>
  }
</app-modal>
